import React from 'react'
import {
  Box,
  Chip,
  Container,
  Grid,
  Paper,
  styled,
  Typography,
  TableContainer,
  Table,
  TableRow,
  TableCell,
  TableBody,
  TableHead,
  useMediaQuery,
} from '@mui/material'
import lightTheme from '../../styles/theme/lightTheme'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPen, faFilePlus, faHeart } from '@fortawesome/pro-regular-svg-icons'
import '../../styles/components/_box.scss'
import lock from '../../assets/images/lock.png'
import { CustomButton, GradientButton } from '../buttons'
import moment from 'moment-timezone'

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: '#FFFFFF',
  height: 435,
  borderRadius: '16px',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
  padding: '24px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  rowGap: '20px',
  transition: 'all 0.3s ease',
  border: '1px solid rgba(0, 0, 0, 0.05)',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
    opacity: 0,
    transition: 'opacity 0.3s ease',
  },
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.12)',
    '&::before': {
      opacity: 1,
    },
  },
}))

const BodyTable = styled(TableBody)(({ theme }) => ({
  backgroundColor: '#FFFFFF',
}))

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  borderBottom: '2px solid rgb(227, 230, 255, 0.5)',
  fontFamily: 'Century Gothic',
  fontSize: 10,
  lineHeight: '140%',
  letterSpacing: '0.2px',
  color: lightTheme.palette.text.primary,

  [theme.breakpoints.up('sm')]: {
    fontSize: 12,
  },
}))

const HeaderTab = styled(TableHead)(({ theme }) => ({
  height: 50,
  backgroundColor: lightTheme.palette.primary.b6,
}))

const TitleHead = styled(TableCell)(({ theme }) => ({
  fontFamily: 'Century Gothic Bold',
  fontSize: 10,
  lineHeight: '140%',
  letterSpacing: '0.2px',
  color: lightTheme.palette.text.primary,

  [theme.breakpoints.up('sm')]: {
    fontSize: 12,
  },
}))

const ItemLong = styled(Paper)(({ theme }) => ({
  backgroundColor: '#FFFFFF',
  padding: '24px',
  borderRadius: '16px',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
  border: '1px solid rgba(0, 0, 0, 0.05)',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 16px 32px rgba(0, 0, 0, 0.12)',
  },
}))

const TitleBox = styled(Typography)(({ theme }) => ({
  fontFamily: 'Century Gothic Bold',
  fontSize: 16,
  lineHeight: '140%',
  letterSpacing: '0.2px',
  color: lightTheme.palette.text.primary,
  textTransform: 'uppercase',
  position: 'relative',
  paddingBottom: '8px',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    width: '40px',
    height: '3px',
    background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
    borderRadius: '2px',
  },
}))

const TitleContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
}))

const IconContainer = styled(Box)(({ theme }) => ({
  width: 36,
  height: 36,
  border: '2px solid rgba(102, 126, 234, 0.3)',
  borderRadius: '50%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  cursor: 'pointer',
  backgroundColor: 'rgba(102, 126, 234, 0.05)',
  transition: 'all 0.3s ease',

  '&:hover': {
    backgroundColor: 'rgba(102, 126, 234, 0.1)',
    border: '2px solid rgba(102, 126, 234, 0.5)',
    transform: 'scale(1.1)',
  },

  '&:active': {
    transform: 'scale(0.95)',
  },
}))

const WrapperIcons = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  columnGap: '8px',
}))

const ValueChip = styled(Chip)(({ theme }) => ({
  margin: '4px',
  backgroundColor: 'rgba(139, 92, 246, 0.08)',
  border: '1px solid rgba(139, 92, 246, 0.2)',
  color: '#8b5cf6',
  fontWeight: 500,
  fontSize: '0.875rem',
  '&:hover': {
    backgroundColor: 'rgba(139, 92, 246, 0.12)',
    transform: 'translateY(-1px)',
  },
}))

const MasonryLayoutCompany = ({
  profil,
  besoinClient,
  commentaireAdmin,
  rendezVous,
  offre,
  ged,
  valeurs,
  displayValeurSection,
  handleOpenDoc,
  handleOpenBesoin,
  handleOpenCommentaire,
  handleOpenValeurs,
  handleOpenOffer,
  displayParams = true,
  feedback,
  candidatures,
}) => {
  const sizeChip = useMediaQuery(lightTheme.breakpoints.down('sm'))
    ? 'small'
    : 'medium'

  return (
    <Container
      maxWidth={'xl'}
      sx={{
        paddingTop: { xs: '16px', sm: '25px' },
        paddingBottom: { xs: '16px', sm: '25px' },
        px: { xs: 2, sm: 3 },
      }}
    >
      <Grid container spacing={{ xs: 2, sm: 3, lg: 4 }}>
        {/* Section profil */}
        <Grid item xs={12} sm={6} lg={6} order={{ sm: 1, lg: 1 }}>
          <Item
            sx={{
              height: '100%',
              position: 'relative',
              overflow: 'hidden',
              padding: { xs: 0, lg: 3 },
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '60px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                display: { xs: 'none', lg: 'flex' },
                justifyContent: 'center',
                alignItems: 'center',
                borderRadius: '16px 16px 0 0',
                boxShadow: '0 8px 32px rgba(102, 126, 234, 0.25)',
                zIndex: 1,
              }}
            >
              <TitleBox style={{ color: '#fff' }}>Espace Entreprise</TitleBox>
            </Box>

            <Box
              sx={{
                pt: { xs: 0, lg: '60px' },
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {profil}
            </Box>
          </Item>
        </Grid>

        <Grid
          item
          xs={12}
          sm={12}
          lg={6}
          order={{ sm: 3, lg: 2 }}
          position={'relative'}
        >
          <Item
            sx={{
              height: '100%',
            }}
            className={''}
          >
            <TitleContainer>
              <TitleBox>Candidatures</TitleBox>
            </TitleContainer>
            <Box sx={{ maxHeight: '400px', overflowY: 'auto' }}>
              {candidatures}
            </Box>
          </Item>
        </Grid>

        {/* Section RDV */}
        <Grid
          item
          xs={12}
          sm={6}
          lg={4}
          order={{ sm: 2, lg: 3 }}
          position={'relative'}
        >
          <Item
            sx={{
              height: '100%',
            }}
          >
            <TitleContainer>
              <TitleBox>Rendez-vous</TitleBox>
            </TitleContainer>

            <Box>{rendezVous}</Box>
          </Item>
        </Grid>

        {/* Section besoin client */}
        <Grid item xs={12} sm={12} lg={8} order={4}>
          <Item
            sx={{
              height: '100%',
            }}
          >
            <TitleContainer>
              <TitleBox>Fiches de poste</TitleBox>
              {displayParams && (
                <WrapperIcons>
                  <IconContainer onClick={handleOpenBesoin}>
                    <FontAwesomeIcon
                      icon={faFilePlus}
                      fontSize={15}
                      color={lightTheme.palette.primary.b2}
                    />
                  </IconContainer>
                </WrapperIcons>
              )}
            </TitleContainer>

            <Box width={'100%'} height={'100%'}>
              {besoinClient}
            </Box>
          </Item>
        </Grid>

        {/* section valeurs */}
        <Grid item xs={12} order={5} display={displayValeurSection}>
          <ItemLong>
            <TitleContainer>
              <TitleBox
                sx={{
                  color:
                    !valeurs || valeurs.length === 0
                      ? lightTheme.palette.error.main
                      : 'initial',
                }}
              >
                Valeurs
                {(!valeurs || valeurs.length === 0) && '*'}
              </TitleBox>
              {displayParams && (
                <WrapperIcons>
                  <IconContainer onClick={handleOpenValeurs}>
                    <FontAwesomeIcon
                      icon={faPen}
                      fontSize={15}
                      color={lightTheme.palette.primary.b2}
                    />
                  </IconContainer>
                </WrapperIcons>
              )}
            </TitleContainer>
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                textAlign: 'center',
                py: 4,
              }}
            >
              {!valeurs || valeurs.length === 0 ? (
                <Box>
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      backgroundColor: 'rgba(244, 67, 54, 0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 2,
                      mx: 'auto',
                    }}
                  >
                    <FontAwesomeIcon
                      icon={faHeart}
                      fontSize={32}
                      color={lightTheme.palette.error.main}
                    />
                  </Box>
                  <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>
                    Aucune valeur
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 3 }}
                  >
                    Aucune valeur renseignée pour cette entreprise
                  </Typography>
                  {displayParams && (
                    <CustomButton
                      handleClick={handleOpenValeurs}
                      sx={{
                        background:
                          'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white',
                        px: 3,
                        py: 1,
                        borderRadius: '25px',
                        textTransform: 'none',
                        fontWeight: 'bold',
                        boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)',
                        '&:hover': {
                          boxShadow: '0 6px 20px rgba(102, 126, 234, 0.4)',
                          transform: 'translateY(-2px)',
                        },
                      }}
                    >
                      Ajouter des valeurs
                    </CustomButton>
                  )}
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {valeurs.map((valeur, i) => (
                    <ValueChip key={i} label={valeur} size={sizeChip} />
                  ))}
                </Box>
              )}
            </Box>
          </ItemLong>
        </Grid>

        {/* Section Feedbacks */}
        <Grid item xs={12} sm={12} order={5}>
          <Item
            sx={{
              height: '100%',
            }}
          >
            <TitleContainer>
              <TitleBox>Feedbacks</TitleBox>
            </TitleContainer>
            <Box sx={{ maxHeight: '400px', overflowY: 'auto' }}>
              <TableContainer>
                <Table>
                  <HeaderTab>
                    <TableRow>
                      <TitleHead>Candidat</TitleHead>
                      <TitleHead>Date d'entretien</TitleHead>
                      <TitleHead>Feedback</TitleHead>
                    </TableRow>
                  </HeaderTab>
                  {feedback &&
                    feedback.map((rdv, i) => (
                      <BodyTable key={i}>
                        {rdv.feedbackCandidat && (
                          <TableRow>
                            <StyledTableCell>
                              {rdv.candidat.profile.prenom}{' '}
                              {rdv.candidat.profile.nom}
                            </StyledTableCell>
                            <StyledTableCell>
                              {moment(rdv.dateDebut)
                                .tz('Europe/Paris')
                                .format('DD/MM/YYYY HH:mm')}
                            </StyledTableCell>
                            <StyledTableCell>
                              {rdv.feedbackCandidat}
                            </StyledTableCell>
                          </TableRow>
                        )}
                      </BodyTable>
                    ))}
                </Table>
              </TableContainer>
            </Box>
          </Item>
        </Grid>

        {/* Section commentaire jobertech */}
        <Grid item xs={12} sm={12} order={6}>
          <Item
            sx={{
              height: '100%',
            }}
          >
            <TitleContainer>
              <TitleBox>Commentaires jobertech</TitleBox>
              {displayParams && (
                <WrapperIcons>
                  <IconContainer onClick={handleOpenCommentaire}>
                    <FontAwesomeIcon
                      icon={faPen}
                      fontSize={15}
                      color={lightTheme.palette.primary.b2}
                    />
                  </IconContainer>
                </WrapperIcons>
              )}
            </TitleContainer>

            <Box>{commentaireAdmin}</Box>
          </Item>
        </Grid>

        {/* Section GED */}
        <Grid item xs={12} sm={12} lg={12} order={7} position={'relative'}>
          <Item
            sx={{
              height: 280,
            }}
            className={ged ? '' : 'blurLayout'}
          >
            <TitleContainer>
              <TitleBox>Documents</TitleBox>
              {displayParams && (
                <WrapperIcons>
                  <IconContainer onClick={handleOpenDoc}>
                    <FontAwesomeIcon
                      icon={faFilePlus}
                      fontSize={15}
                      color={lightTheme.palette.primary.b2}
                    />
                  </IconContainer>
                </WrapperIcons>
              )}
            </TitleContainer>

            <Box overflow={'auto'} width={'100%'} height={'100%'}>
              {ged}
            </Box>
          </Item>

          {!ged && (
            <img
              src={lock}
              alt={'Jobertech - not access'}
              style={{
                width: 150,
                height: 150,
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-40%, -45%)',
              }}
            />
          )}
        </Grid>

        {/* Section Offres */}
        <Grid item xs={12} sm={12} lg={12} order={8} position={'relative'}>
          <Item
            sx={{
              display: 'flex',
              flexDirection: 'column',
              rowGap: '20px',
              justifyContent: 'space-between',
            }}
          >
            <TitleContainer>
              <TitleBox>Historique des achats</TitleBox>
            </TitleContainer>

            <Box overflow={'auto'} width={'100%'}>
              {offre}
            </Box>
            {displayParams && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  rowGap: '15px',
                }}
              >
                <GradientButton onClick={handleOpenOffer}>
                  Proposer une offre
                </GradientButton>
              </Box>
            )}
          </Item>
        </Grid>
      </Grid>
    </Container>
  )
}

export default MasonryLayoutCompany
